<script lang="ts">
  import type { PortCallTypeRef } from '@gat-ship/types'

  export interface ShowEditPortCallTypeParams {
    entry: PortCallTypeRef
  }
</script>

<script setup lang="ts">
  import { ref } from 'vue'
  import { IconLayers, IconTrash } from '@gat-ship/icons'
  import {
    FieldGroup,
    FormField,
    FormSubmit,
    InputGroupInput,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { usePortCallTypes } from '../../../../composables/usePortCallTypes'

  const isOpen = defineModel<boolean>('open', { default: false })
  const service = usePortCallTypes()

  const editingId = ref<string | undefined>(undefined)

  const schema = z.object({
    name: z.string().min(1, 'Required').max(60, 'Max 60 characters')
  })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: { name: '' } as FormValues,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      if (!editingId.value) return
      await service.update(editingId.value, { name: value.name })
      isOpen.value = false
    }
  })

  async function handleDelete() {
    if (!editingId.value) return
    await service.remove(editingId.value)
    isOpen.value = false
  }

  function showModal({ entry }: ShowEditPortCallTypeParams) {
    editingId.value = entry.id
    form.reset({ name: entry.name })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconLayers class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Edit portcall type
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        Rename this port call classification or remove it from the list.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button variant="ghost" @click="handleDelete">
        <IconTrash class="size-3.5" />
        Delete
      </Button>
      <Button class="ml-auto" variant="ghost" @click="isOpen = false">
        Cancel
      </Button>
      <FormSubmit :form="form" label="Save" :require-dirty="false" />
    </ModalFooter>
  </modal>
</template>
